import type { ShellNavItem, ShellNavSection } from "./types";

interface Pattern {
  path: string;
  query: [string, string][];
}

function parse(url: string): Pattern {
  const [rawPath, rawQuery = ""] = url.split("?");
  const path = rawPath.length > 1 ? rawPath.replace(/\/+$/, "") : rawPath;
  return { path, query: [...new URLSearchParams(rawQuery).entries()] };
}

/**
 * How well a pattern fits the current location: -1 when it doesn't, else a
 * score where a longer path wins, then more matching query values. A path
 * matches itself or any sub-path ("/fr/admin/payments" covers
 * "/fr/admin/payments/history"), never a sibling that only shares a prefix.
 */
function score(pattern: Pattern, pathname: string, search: URLSearchParams): number {
  const current = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  const pathOk = current === pattern.path || current.startsWith(`${pattern.path}/`);
  if (!pathOk) return -1;
  for (const [key, value] of pattern.query) {
    if (search.get(key) !== value) return -1;
  }
  return pattern.path.length * 10 + pattern.query.length;
}

/** The single entry to highlight: the most specific match wins. */
export function findActiveItem(sections: ShellNavSection[], pathname: string, search: string): ShellNavItem | undefined {
  const params = new URLSearchParams(search);
  let best: { item: ShellNavItem; score: number } | undefined;
  for (const section of sections) {
    for (const item of section.items) {
      for (const url of [item.href, ...(item.match ?? [])]) {
        const s = score(parse(url), pathname, params);
        if (s >= 0 && (!best || s > best.score)) best = { item, score: s };
      }
    }
  }
  return best?.item;
}
